/** Display, comparison and CSV of reference table entries (proposal §5). */
import {
  type EntryResponse,
  type EntryValue,
  fileValue,
  formatMoney,
  formatNumber,
  type LookupData,
  parseDecimal,
  type TableColumn,
} from '@k-management/shared';
import { formatDate } from '../../lib/format.js';

/**
 * A value as the page shows it («1.234,50 €»). In a file (`forFile`) numbers
 * keep the comma but lose the thousands dot and the symbol, so that Excel and a
 * new import read them back as they were.
 */
export function formatValue(
  column: TableColumn,
  value: EntryValue | undefined,
  forFile = false,
  lookups?: LookupData,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (forFile) return fileValue(column, value);
  // A code of another table reads as its name.
  if (column.lookup && lookups) {
    const found = lookups[column.lookup.table]?.find((entry) => entry.code === value);
    if (found) return found.description;
  }
  switch (column.type) {
    case 'boolean':
      return value ? 'Sì' : 'No';
    case 'money':
    case 'number': {
      const number = parseDecimal(typeof value === 'number' ? value : String(value));
      if (number === null) return String(value);
      return column.type === 'money' ? formatMoney(number) : formatNumber(number);
    }
    case 'date':
      return formatDate(String(value));
    case 'tags':
      return Array.isArray(value) ? value.join(', ') : String(value);
    default:
      return String(value);
  }
}

export type EntryChange =
  | { kind: 'added' }
  | { kind: 'removed'; before: EntryResponse }
  | { kind: 'changed'; before: EntryResponse; fields: string[] };

/** What changed from `base` to `shown`, by code; `fields` are "description", "active"… or "values.<key>". */
export function compareEntries(
  base: readonly EntryResponse[],
  shown: readonly EntryResponse[],
): Map<string, EntryChange> {
  const result = new Map<string, EntryChange>();
  const before = new Map(base.map((entry) => [entry.code, entry]));
  for (const entry of shown) {
    const previous = before.get(entry.code);
    if (!previous) {
      result.set(entry.code, { kind: 'added' });
      continue;
    }
    const fields = (['description', 'active', 'isDefault', 'hidden'] as const).filter(
      (field) => previous[field] !== entry[field],
    ) as string[];
    const keys = new Set([...Object.keys(previous.values), ...Object.keys(entry.values)]);
    for (const key of keys) {
      if (
        JSON.stringify(previous.values[key] ?? null) !== JSON.stringify(entry.values[key] ?? null)
      ) {
        fields.push(`values.${key}`);
      }
    }
    if (fields.length > 0) result.set(entry.code, { kind: 'changed', before: previous, fields });
  }
  for (const entry of base) {
    if (!shown.some((item) => item.code === entry.code)) {
      result.set(entry.code, { kind: 'removed', before: entry });
    }
  }
  return result;
}

/** CSV with semicolons, as Excel expects in Italy. */
export function downloadText(fileName: string, text: string): void {
  // BOM: Excel reads accented letters correctly.
  const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
